import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import { Box, CardMedia, Typography } from "@mui/material";
import { convertString2JSON, StorageUtil, toCamel, toSnake } from "utils";
import { ImageConstant, KeyConstant, LangConstant, SystemConstant } from "const";
import { AppDrawer, InfiniteScroll } from "components";
import { getInteractor } from "services/local.service";
import { ConversationActions, SystemActions, SystemSelectors } from "redux-store";
import NotificationInviteItem from "./NotificationInviteItem";
import NotificationTextItem from "./NotificationTextItem";
import NotificationOwnerItem from "./NotificationOwnerItem";
import NotificationOTPItem from "./NotificationOTPItem";
import { getCommonLang } from "utils/lang.utils";
import { orderBy, uniqBy } from "lodash";
import { getSavedServer } from "utils/branch.utils";
import NotificationBranchReqItem from "./NotificationBranchReqItem";

const NotificationDrawer = ({ onClose, ...otherProps }) => {
  const classes = useStyles();
  const { t: getLabel } = useTranslation(LangConstant.NS_HOME);
  const dispatch = useDispatch();
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const branchId = StorageUtil.getItem(KeyConstant.KEY_BRANCH_ID, prefixKey);
  const fetchingDataRef = useRef(true);

  const newNotification = useSelector(SystemSelectors.getNewNotification);

  const [displayNotices, setDisplayNotices] = useState([]);
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: SystemConstant.LIMIT_PAGING_DEFAULT,
    isEnd: false,
  });

  const handleScrollToBottom = () => {
    if (!pagination.isEnd && !fetchingDataRef.current) {
      handleGetNotice();
    }
  };

  const convertInviteNotification = (inviteNoticeList = [], newInvitation = null) => {
    if (newInvitation) inviteNoticeList = [...inviteNoticeList, newInvitation];
    const maxedByBranchId = {};
    inviteNoticeList.forEach(item => {
      if (!maxedByBranchId[item.branchId] || item.created > maxedByBranchId[item.branchId].created) {
        maxedByBranchId[item.branchId] = item;
      }
    });
    if (Object.keys(maxedByBranchId).length > 0) return Object.values(maxedByBranchId);
    return [];
  };

  const handleGetNotice = async () => {
    fetchingDataRef.current = true;
    const savedServer = getSavedServer();

    const localNoticeList = toCamel(
      await getInteractor(prefixKey).LocalNotificationService.getNormalNotification({
        branch_id: branchId,
        limit: pagination.limit,
        offset: pagination.offset,
      }),
    );

    let notificationList = localNoticeList.filter(item => !REFACTOR_NOTIFICATION.includes(item.type));

    const branchRequestNoticeList = localNoticeList.filter(
      item => item.type === SystemConstant.NotifyType.BRANCH_REQUEST,
    );

    if (branchRequestNoticeList.length > 0) {
      const branchReqRefactor = await convertBranchReqNotice(prefixKey, branchRequestNoticeList);
      notificationList = notificationList.concat(branchReqRefactor);
    }

    if (savedServer.type === SystemConstant.SERVER_TYPE.server) {
      const tmpRequestList = localNoticeList.filter(item => item.type === SystemConstant.NotifyType.INVITE);
      const invitationList = convertInviteNotification(tmpRequestList);
      notificationList = notificationList.concat(invitationList);
    }

    notificationList = orderBy(notificationList, ["created"], ["desc"]);
    setDisplayNotices(state => uniqBy(state.concat(notificationList), "id"));
    setPagination(state => ({
      ...state,
      offset: state.offset + localNoticeList.length,
      isEnd: localNoticeList.length < state.limit,
    }));

    const notSeen = localNoticeList.filter(noticeItem => noticeItem.status !== SystemConstant.NOTIFICATION_STATUS.read);

    if (notSeen.length > 0) {
      getInteractor().LocalNotificationService.save(
        notSeen.map(item =>
          toSnake({
            ...item,
            status: SystemConstant.NOTIFICATION_STATUS.read,
          }),
        ),
      );
      dispatch(
        ConversationActions.updateMessageStatus({
          notifyIds: notSeen.map(noticeItem => noticeItem.id),
          status: SystemConstant.NOTIFICATION_STATUS.read,
        }),
      );
    }

    fetchingDataRef.current = false;
  };

  useEffect(() => {
    handleGetNotice();
  }, []);

  useEffect(() => {
    const isFirstFetchData = pagination.offset === 0 && fetchingDataRef.current;
    if (newNotification && newNotification.id && !isFirstFetchData) {
      // New notification for invitation
      if (newNotification.type === SystemConstant.NotifyType.INVITE && branchId === SystemConstant.GLOBAL_BRANCH_ID) {
        const notificationList = displayNotices.filter(item => item.type !== SystemConstant.NotifyType.INVITE);
        const tmpInviteNotices = displayNotices.filter(item => item.type === SystemConstant.NotifyType.INVITE) || [];
        const invitationNotice = convertInviteNotification(tmpInviteNotices, newNotification);
        setDisplayNotices(invitationNotice.concat(notificationList));
      }

      // New notification for branch request
      if (newNotification.type === SystemConstant.NotifyType.BRANCH_REQUEST && newNotification.branchId === branchId) {
        const existReqIndex = displayNotices.findIndex(item => item.parent === newNotification.parent);
        if (existReqIndex >= 0) {
          const existReq = displayNotices[existReqIndex];
          const newBranchReq = { ...existReq, state: newNotification.state, options: newNotification.options };
          const newDisplayNotice = [...displayNotices];
          newDisplayNotice[existReqIndex] = refactorBranchReqNotification(newBranchReq);
          setDisplayNotices(newDisplayNotice);
        } else {
          setDisplayNotices(state => [refactorBranchReqNotification(newNotification), ...state]);
        }
      }

      dispatch(
        SystemActions.systemSet({
          newNotification: null,
        }),
      );
    }
  }, [newNotification]);

  const isEmptyNotice = !Boolean(fetchingDataRef.current) && displayNotices.length === 0;
  return (
    <AppDrawer title={getCommonLang("TXT_NOTIFICATION")} onClose={onClose} {...otherProps}>
      <InfiniteScroll className={classes.chatList} onScrollToBottom={handleScrollToBottom}>
        {isEmptyNotice && (
          <Box className={classes.emptyChatList}>
            <CardMedia component="img" src={ImageConstant.WelcomeImage} className={classes.emptyImage} />
            <Typography className={classes.emptyMessage}>{getLabel(LangConstant.TXT_NO_NOTIFICATION)}</Typography>
          </Box>
        )}

        <NotificationList notificationList={displayNotices} onClose={onClose} />
      </InfiniteScroll>
    </AppDrawer>
  );
};

export default NotificationDrawer;

const REFACTOR_NOTIFICATION = [SystemConstant.NotifyType.INVITE, SystemConstant.NotifyType.BRANCH_REQUEST];

const refactorBranchReqNotification = notification => {
  const branchRequest = notification.options ? convertString2JSON(notification.options, {}) : {};
  return { ...notification, requestNotice: branchRequest };
};

const convertBranchReqNotice = async (prefixKey, branchReqList = [], newBranchReq = null) => {
  if (newBranchReq) branchReqList = [...branchReqList, newBranchReq];
  const refactorList = [];
  for (let index = 0; index < branchReqList.length; index++) {
    const branchReq = branchReqList[index];
    const latestBranchReq = await getInteractor(prefixKey).LocalNotificationService.findOne(
      { parent: branchReq.parent },
      "ORDER BY created DESC",
    );
    refactorList.push(
      refactorBranchReqNotification({
        ...branchReq,
        state: latestBranchReq.state || branchReq.state,
        options: latestBranchReq.options || branchReq.options,
      }),
    );
  }

  return uniqBy(refactorList, "parent");
};

const NotificationList = ({ notificationList, onClose }) => {
  if (!Array.isArray(notificationList) || notificationList.length === 0) return null;
  return notificationList.map(notice => (
    <Box key={notice.id}>
      {notice.type === SystemConstant.NotifyType.INVITE && <NotificationInviteItem data={notice} onAccept={onClose} />}

      {notice.type === SystemConstant.NotifyType.NOTIFICATION && <NotificationTextItem data={notice} />}

      {notice.type === SystemConstant.NotifyType.OWNER_NOTIFICATION && <NotificationOwnerItem data={notice} />}

      {notice.type === SystemConstant.NotifyType.SMARTOTP && <NotificationOTPItem data={notice} />}

      {notice.type === SystemConstant.NotifyType.BRANCH_REQUEST && <NotificationBranchReqItem data={notice} />}
    </Box>
  ));
};

const useStyles = makeStyles(theme => ({
  chatList: {
    height: "100%",
    overflow: "auto",
  },

  emptyChatList: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    padding: "0 56px",
    borderTop: "0.5px solid " + theme.palette.divider,
  },

  emptyImage: {
    padding: "0 14px",
  },

  emptyMessage: {
    fontSize: "0.9375rem",
    fontWeight: 600,
    marginTop: 16,
    lineHeight: "1.25rem",
  },
}));
